import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import type { PropsWithChildren } from 'react';

import { Arrow } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';
import { UsdtPrecision } from '@flipster/utils/constants/currency';

import { formBunja } from './bunja/form';

interface PnlRowProps {
  label: string;
  pnl: string | null | undefined;
  roi: string | null | undefined;
}

const PnlRow = ({ label, pnl, roi }: PnlRowProps) => {
  const canShow = pnl != null;

  return (
    <div className={hstack({ gap: 2, flexWrap: 'wrap' })}>
      <span
        className={css({
          textStyle: 'Caption/L/Monospace/Regular',
          color: 'content.default.level-3',
          flexShrink: 0,
          minW: 30,
        })}
      >
        {label}
      </span>
      {canShow ? (
        <>
          <Formatted.Number
            value={pnl}
            withPlus
            withColor
            toFixed
            precision={UsdtPrecision}
            css={{ textStyle: 'Caption/L/Monospace/Regular' }}
          />
          <Formatted.Percent
            value={roi}
            withPlus
            withColor
            withParentheses
            toFixed
            precision={UsdtPrecision}
            css={{ textStyle: 'Caption/L/Monospace/Regular' }}
          />
        </>
      ) : (
        <span className={css({ textStyle: 'Caption/L/Monospace/Regular', color: 'content.default.level-5' })}>-</span>
      )}
    </div>
  );
};

interface TpSlEstPnlTooltipProps {
  field: 'tp' | 'sl';
}

const TpSlEstPnlTooltip = ({ field, children }: PropsWithChildren<TpSlEstPnlTooltipProps>) => {
  const { focusedTpSlFieldAtom } = useBunja(formBunja);
  const focusedField = useAtomValue(focusedTpSlFieldAtom);

  // est PnL(tpslEstPnlAtom)은 라이브 가격 파생이라 매 tick 변한다. 포커스된(툴팁이 열린)
  // 입력칸에서만 구독하도록 패널을 별도 컴포넌트로 분리한다. 닫힌 상태에서는 구독 자체가
  // 없어 매 tick 리렌더도, 파생 atom 재계산(BigNumber PnL)도 발생하지 않는다.
  const isOpen = focusedField === field;

  return (
    <div className={css({ pos: 'relative' })}>
      {children}
      {isOpen && <EstPnlPanel field={field} />}
    </div>
  );
};

export default TpSlEstPnlTooltip;

/** 툴팁이 열린 동안에만 마운트되어 est PnL을 구독한다. */
const EstPnlPanel = ({ field }: TpSlEstPnlTooltipProps) => {
  const t = useTranslations();
  const { tpslEstPnlAtom } = useBunja(formBunja);
  const estPnl = useAtomValue(tpslEstPnlAtom);
  const data = estPnl?.[field];

  return (
    <div className={panel}>
      <div className={vstack({ gap: 2, alignItems: 'flex-start' })}>
        <PnlRow label={t('Autogen_common.r251126.s_Long')} pnl={data?.long?.pnl} roi={data?.long?.roi} />
        <PnlRow label={t('Autogen_common.r251126.s_Short')} pnl={data?.short?.pnl} roi={data?.short?.roi} />
      </div>
      <div className={arrow}>
        <Arrow />
      </div>
    </div>
  );
};

const panel = css({
  pos: 'absolute',
  left: 0,
  bottom: '100%',
  zIndex: 'tooltip',
  py: 8,
  px: 12,
  rounded: 8,
  '--bg-color': '{colors.background.default.level-4}',
  bgColor: 'var(--bg-color)',
  color: 'content.default.level-5',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
});

const arrow = css({
  pos: 'absolute',
  top: 'calc(100% - 1px)',
  left: 16,
  lineHeight: 0,
});
